'use client';

import { useEffect, useRef, useState } from 'react';
import { CustomImage as Image } from '../..';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import gsap from 'gsap';
import styles from './TechnicalSpecs.module.css';

// Types for dynamic content
interface ImageInfo {
  src: string;
  alt: string;
  textBold: string;
  textNormal: string;
}

interface AccordionItem {
  title: React.ReactNode;
  content: React.ReactNode;
}

interface TechnicalSpecsProps {
  className?: string;
  htmlId?: string;
  title?: React.ReactNode;
  images?: ImageInfo[];
  accordionItems?: AccordionItem[];
  imageBackgroundColor?: string;
}

const accordionItems = [
  {
    title: (
      <>
        <span className="bold">Model</span>
      </>
    ),
    content: (
      <>
        <div className="content">
          <span>Diesel Ultrahuman Ring</span>
        </div>
      </>
    ),
  },
  {
    title: (
      <>
        <span className="bold">Body</span>
      </>
    ),
    content: (
      <>
        <div className="content">
          <span className="bold">Dimensions</span>
          <span>Width - 8.2mm</span>
          <span>Thickness - 3.4 - 4.2 mm (varies with size)</span>
        </div>
        <div className="content">
          <span className="bold">Weight</span>
          <span>2.9 - 4.1 g (varies with size)</span>
        </div>
      </>
    ),
  },
  {
    title: (
      <>
        <span className="bold">Material</span>
      </>
    ),
    content: (
      <>
        <div className="content">
          <span>
            The outer shell is made from fighter jet grade Titanium reinforced
            with Tungsten Carbide Carbon coating, making it resistant to
            everyday wear and tear
          </span>
          <span>
            The inners of the ring is coated with medical-grade hypoallergenic
            epoxy resin, making it comfortable to wear at all times
          </span>
        </div>
      </>
    ),
  },
  {
    title: (
      <>
        <span className="bold">Connectivity</span>
      </>
    ),
    content: (
      <>
        <div className="content">
          <span>Bluetooth Low Energy (BLE5)</span>
          <span>Automatic firmware updates via the Ultrahuman App</span>
          <span>
            Compatible with iPhones running iOS 15 or later and Android devices
            running Android 6 or later.
          </span>
          <span>EMF radiation safe</span>
          <span>Transmission Frequency: 2.4 GHz</span>
        </div>
      </>
    ),
  },
  {
    title: (
      <>
        <span className="bold">Sensors</span>
      </>
    ),
    content: (
      <div className="content">
        <span>Infrared Photoplethysmography (PPG) sensor</span>
        <span>Non-contact medical-grade skin temperature sensor</span>
        <span>6-axis motion sensors</span>
        <span>Red LEDs (heart rate monitoring and oxygen saturation)</span>
        <span>Green LEDs (heart rate monitoring)</span>
        <span>Infrared LEDs (heart rate monitoring)</span>
      </div>
    ),
  },
  {
    title: (
      <>
        <span className="bold">Battery</span>
      </>
    ),
    content: (
      <div className="content">
        <span>Rechargeable, non-replaceable 24mAh LiPo battery</span>
        <span>Lasts 4 to 6 days on a full charge</span>
        <span>180 mins to fully charge the Ring from 0% to 100%</span>
      </div>
    ),
  },
];

const TwoImageSection = ({
  className,
  images,
  imageBackgroundColor,
}: {
  className?: string;
  images?: ImageInfo[];
  imageBackgroundColor?: string;
}) => {
    const container = useRef<HTMLDivElement | null>(null);
    const image1 = useRef<HTMLImageElement | null>(null);
    const image2 = useRef<HTMLImageElement | null>(null);

    const gsapContext = useGsapContext();

    // Default images for backward compatibility
    const defaultImages: ImageInfo[] = [
      {
        src: 'web_v2/uh-x-diesel/buy-page/tech-specs/tech-specs-1.png/w_1920',
        alt: 'Diesel Ultrahuman Ring',
        textBold: '2.9 g light.',
        textNormal: '10x lighter than a typical smartwatch.',
      },
      {
        src: 'web_v2/uh-x-diesel/buy-page/tech-specs/tech-specs-2.png/w_1920',
        alt: 'Diesel Ultrahuman Ring',
        textBold: '3.4 mm sleek.',
        textNormal: 'Discreet design for everyday use.',
      },
    ];

    const displayImages = images || defaultImages;

    const animInit = () => {
      if (!window || !image1.current || !image2.current) return;

      gsapContext.add(() => {
        const tl = gsap.timeline({
          defaults: {
            duration: 1,
          },
          scrollTrigger: {
            trigger: container.current,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 0,
          },
        });

        tl.fromTo(
          image1.current,
          {
            y: 200,
            scale: 1,
          },
          {
            y: -100,
            scale: 1.2,
          },
        );

        tl.fromTo(
          image2.current,
          {
            y: 200,
            scale: 2.5,
          },
          {
            y: -100,
            scale: 1.5,
          },
          '-=1',
        );
      });
    };

    useIsomorphicLayoutEffect(() => {
      const timer = setTimeout(() => {
        animInit();
      }, 100);

      return () => {
        if (timer) clearTimeout(timer);
      };
    }, []);

    return (
      <section
        className={
          className ? `${styles.twoImageRoot} ${className}` : styles.twoImageRoot
        }
        ref={container}
      >
        <div className="content">
          <div className="img-info-element">
            <div
              className="img-container"
              style={{ background: imageBackgroundColor || '#000' }}
            >
              <Image
                ref={image1}
                src={displayImages[0]?.src}
                fill
                alt={displayImages[0]?.alt}
              />
            </div>
            <p>
              <b>{displayImages[0]?.textBold}</b> {displayImages[0]?.textNormal}
            </p>
          </div>
          {displayImages[1] && (
            <div className="img-info-element">
              <div
                className="img-container"
                style={{ background: imageBackgroundColor || '#000' }}
              >
                <Image
                  ref={image2}
                  src={displayImages[1].src}
                  fill
                  alt={displayImages[1].alt}
                />
              </div>
              <p>
                <b>{displayImages[1].textBold}</b> {displayImages[1].textNormal}
              </p>
            </div>
          )}
        </div>
      </section>
    );
};

export const TechnicalSpecs = ({
  className,
  htmlId,
  title,
  images,
  accordionItems: customAccordionItems,
  imageBackgroundColor,
}: TechnicalSpecsProps) => {
    const rootRef = useRef<HTMLDivElement>(null);

    return (
      <section
        className={
          className
            ? `${styles.technicalSpecsRoot} ${className}`
            : styles.technicalSpecsRoot
        }
        id={htmlId}
        ref={rootRef}
      >
        <h3>{title || 'Technical Specifications'}</h3>
        <TwoImageSection
          images={images}
          imageBackgroundColor={imageBackgroundColor}
        />
        <TechnicalSpec accordionItems={customAccordionItems} />
      </section>
    );
};

const TechnicalSpec = ({
  className,
  accordionItems: customAccordionItems,
}: {
  className?: string;
  accordionItems?: AccordionItem[];
}) => {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
      function handleResize() {
        if (window.innerWidth < 768) {
          setIsMobile(true);
        } else {
          setIsMobile(false);
        }
      }
      handleResize();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
      <div
        className={`${styles.technicalSpec} u-grid${
          className ? ` ${className}` : ''
        }`}
      >
        <div>
          {!isMobile ? (
            <DesktopTable accordionItems={customAccordionItems} />
          ) : (
            <MobileAccordion accordionItems={customAccordionItems} />
          )}
        </div>
      </div>
    );
};

export const DesktopTable = ({
  className,
  accordionItems: customAccordionItems,
}: {
  className?: string;
  accordionItems?: AccordionItem[];
}) => {
    const displayItems = customAccordionItems || accordionItems;

    return (
      <table
        className={
          className ? `${styles.desktopTable} ${className}` : styles.desktopTable
        }
      >
        <tbody>
          {displayItems.map((item, index) => (
            <tr key={index}>
              <td>
                {/* <span className="bold">{String(index + 1).padStart(2, '0')}</span> */}
              </td>
              <td>
                <span className="bold">{item.title}</span>
              </td>
              <td>
                <div className="content">{item.content}</div>
              </td>
              <td>
                <div></div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
};

type AccordionProps = {
  className?: string;
  data: Array<{
    title: React.ReactNode;
    content: React.ReactNode;
  }>;
};

const Accordion = ({ className, data }: AccordionProps) => {
  const [openIndex, setOpenIndex] = useState(0);

  const handleClick = (index: number) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    index: number,
  ) => {
    if (event.keyCode === 13) setOpenIndex(openIndex === index ? -1 : index);
  };

  return (
    <div
      className={`${styles.accordionRoot}${
        className ? ` ${className}` : ''
      } accordion`}
    >
      {data.map((data, index) => {
        const { title, content } = data;
        return (
          <div
            className={`accordion-item ${openIndex === index ? 'active' : ''} `}
            key={index}
          >
            <div
              className="accordion-header"
              onClick={() => handleClick(index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              role="presentation"
              tabIndex={0}
            >
              <>
                {title}
                <span className="icon">{openIndex === index ? '-' : '+'}</span>
              </>
            </div>
            <div className="accordion-body">{content}</div>
          </div>
        );
      })}
    </div>
  );
};

export const MobileAccordion = ({
  className,
  accordionItems: customAccordionItems,
}: {
  className?: string;
  accordionItems?: AccordionItem[];
}) => {
  const displayItems = customAccordionItems || accordionItems;

  return (
    <div
      className={
        className ? `${styles.mobileAccordion} ${className}` : styles.mobileAccordion
      }
    >
      <Accordion data={displayItems} />
    </div>
  );
};
